import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import FixedSidebar from '../../components/ui/FixedSidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import CalendarView from './components/CalendarView';
import SchedulingForm from './components/SchedulingForm';
import UpcomingInterviews from './components/UpcomingInterviews';
import BulkScheduling from './components/BulkScheduling';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import authService from '../../services/authService';
import { hasActionAccess, hasFeatureAccess } from '../../utils/roleBasedAccess';

const InterviewScheduling = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState('');
  const [viewMode, setViewMode] = useState('month');
  const [interviews, setInterviews] = useState([]);
  const [editingInterview, setEditingInterview] = useState(null);
  const [showBulkScheduling, setShowBulkScheduling] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  // Load current user on component mount
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const user = authService.getStoredUser() || await authService.getCurrentUser();
        setCurrentUser(user);
      } catch (error) {
        console.warn('[InterviewScheduling] Could not load current user:', error);
      }
    };

    loadCurrentUser();
  }, []);

  // Mock notification counts
  const notificationCounts = {
    candidates: 12,
    processing: 5,
    interviews: 8,
    total: 25
  };

  // Mock interviews data
  const mockInterviews = [
    {
      id: 'int_1',
      candidateId: 'c1',
      candidateName: 'Sarah Johnson',
      interviewerId: 'i1',
      interviewerName: 'John Smith',
      dateTime: new Date(2025, 7, 27, 14, 0)?.toISOString(),
      type: 'video',
      duration: 60,
      status: 'scheduled',
      meetingLink: 'https://zoom.us/j/123456789',
      notes: 'Technical interview focusing on React and Node.js experience'
    },
    {
      id: 'int_2',
      candidateId: 'c2',
      candidateName: 'Michael Chen',
      interviewerId: 'i2',
      interviewerName: 'Maria Garcia',
      dateTime: new Date(2025, 7, 28, 10, 30)?.toISOString(),
      type: 'phone',
      duration: 45,
      status: 'confirmed',
      notes: 'Initial screening call'
    },
    {
      id: 'int_3',
      candidateId: 'c3',
      candidateName: 'Emily Rodriguez',
      interviewerId: 'i3',
      interviewerName: 'Robert Wilson',
      dateTime: new Date(2025, 7, 28, 15, 0)?.toISOString(),
      type: 'in-person',
      duration: 90,
      status: 'scheduled',
      location: 'Conference Room A, Building 1',
      notes: 'Design portfolio review and team fit assessment'
    },
    {
      id: 'int_4',
      candidateId: 'c4',
      candidateName: 'David Thompson',
      interviewerId: 'i1',
      interviewerName: 'John Smith',
      dateTime: new Date(2025, 7, 29, 11, 0)?.toISOString(),
      type: 'video',
      duration: 60,
      status: 'pending',
      meetingLink: 'https://meet.google.com/abc-defg-hij',
      notes: 'Backend architecture discussion'
    },
    {
      id: 'int_5',
      candidateId: 'c5',
      candidateName: 'Lisa Wang',
      interviewerId: 'i4',
      interviewerName: 'Jennifer Lee',
      dateTime: new Date(2025, 7, 30, 9, 30)?.toISOString(),
      type: 'video',
      duration: 75,
      status: 'scheduled',
      meetingLink: 'https://teams.microsoft.com/l/meetup-join/xyz',
      notes: 'Product management case study and stakeholder management'
    },
    {
      id: 'int_6',
      candidateId: 'c6',
      candidateName: 'James Wilson',
      interviewerId: 'i3',
      interviewerName: 'Robert Wilson',
      dateTime: new Date(2025, 8, 2, 14, 30)?.toISOString(),
      type: 'in-person',
      duration: 120,
      status: 'scheduled',
      location: 'Conference Room B, Building 2',
      notes: 'DevOps technical assessment and infrastructure discussion'
    }
  ];

  useEffect(() => {
    // Simulate loading interviews
    const loadInterviews = async () => {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setInterviews(mockInterviews);
      setIsLoading(false);
    };

    loadInterviews();
  }, []);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime('');
  };

  const handleTimeSlotSelect = (date, time) => {
    setSelectedDate(date);
    setSelectedTime(time);
  };

  const handleInterviewClick = (interview) => {
    setEditingInterview(interview);
  };

  const handleScheduleInterview = (interviewData) => {
    if (editingInterview) {
      // Update existing interview
      setInterviews(prev => prev?.map(interview => 
        interview?.id === editingInterview?.id 
          ? { ...interviewData, id: editingInterview?.id }
          : interview
      ));
      setEditingInterview(null);
    } else {
      // Add new interview
      setInterviews(prev => [...prev, interviewData]);
    }
    
    // Reset form state
    setSelectedTime('');
  };

  const handleEditInterview = (interview) => {
    setEditingInterview(interview);
    const interviewDate = new Date(interview.dateTime);
    setSelectedDate(interviewDate);
    setSelectedTime(interviewDate?.toTimeString()?.substring(0, 5));
  };

  const handleRescheduleInterview = (interview) => {
    setEditingInterview(interview);
    // Could open a specific reschedule modal or use the main form
  };

  const handleCancelInterview = (interview) => {
    if (window.confirm(`Are you sure you want to cancel the interview with ${interview?.candidateName}?`)) {
      setInterviews(prev => prev?.map(int => 
        int?.id === interview?.id 
          ? { ...int, status: 'cancelled' }
          : int
      ));
    }
  };

  const handleSendReminder = (interview) => {
    // Simulate sending reminder
    alert(`Reminder sent to ${interview?.candidateName} and ${interview?.interviewerName}`);
  };

  const handleBulkSchedule = (scheduleData) => {
    const newInterviews = scheduleData?.map((item, index) => ({
      id: `bulk_${Date.now()}_${index}`,
      candidateId: item?.candidateId,
      candidateName: item?.candidateName,
      interviewerId: item?.interviewerId,
      interviewerName: 'John Smith', // This would come from the interviewer selection
      dateTime: item?.dateTime,
      type: item?.type,
      duration: item?.duration,
      status: 'scheduled',
      location: item?.location,
      meetingLink: item?.meetingLink,
      notes: item?.notes
    }));

    setInterviews(prev => [...prev, ...newInterviews]);
    setShowBulkScheduling(false);
  };

  const handleCancelEdit = () => {
    setEditingInterview(null);
    setSelectedTime('');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header currentUser={currentUser} notificationCounts={notificationCounts} />
        <div className="pt-16">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <Icon name="Loader" size={48} className="mx-auto text-primary animate-spin mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Loading Interview Schedule</h3>
              <p className="text-muted-foreground">Please wait while we fetch your interviews...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        currentUser={currentUser} 
        notificationCounts={notificationCounts}
        onNavigate={(path) => console.log('Navigate to:', path)}
      />
      <FixedSidebar currentPath="/interview-scheduling" />
      <main className="ml-72 pt-16">
        <div className="p-6">
          <Breadcrumb customBreadcrumbs={[
            { label: 'Dashboard', href: '/' },
            { label: 'Interview Scheduling', href: '/interview-scheduling' }
          ]} />
          
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Interview Scheduling</h1>
              <p className="text-muted-foreground">
                Coordinate interviews with candidates and manage your team's schedule
              </p>
            </div>
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              {/* Bulk Schedule - Only for Admin, HR Manager, Recruiter */}
              {hasActionAccess(currentUser?.role, 'interviews', 'schedule') && 
               ['admin', 'hr_manager', 'recruiter'].includes(currentUser?.role) && (
                <Button
                  variant="outline"
                  iconName="Users"
                  iconPosition="left"
                  onClick={() => setShowBulkScheduling(true)}
                >
                  Bulk Schedule
                </Button>
              )}
              
              {/* Schedule Interview - For all roles with interview schedule access */}
              {hasActionAccess(currentUser?.role, 'interviews', 'schedule') && (
                <Button
                  variant="default"
                  iconName="CalendarPlus"
                  iconPosition="left"
                  onClick={() => {
                    setSelectedDate(new Date());
                    setSelectedTime('');
                    setEditingInterview(null);
                  }}
                >
                  Schedule Interview
                </Button>
              )}
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface rounded-lg border border-border p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Calendar" size={20} color="var(--color-primary)" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Scheduled</p>
                  <p className="text-2xl font-bold text-foreground">
                    {interviews?.filter(i => i?.status === 'scheduled')?.length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-surface rounded-lg border border-border p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Confirmed</p>
                  <p className="text-2xl font-bold text-foreground">
                    {interviews?.filter(i => i?.status === 'confirmed')?.length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-surface rounded-lg border border-border p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={20} color="var(--color-warning)" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p className="text-2xl font-bold text-foreground">
                    {interviews?.filter(i => i?.status === 'pending')?.length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-surface rounded-lg border border-border p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="Users" size={20} color="var(--color-accent)" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">This Week</p>
                  <p className="text-2xl font-bold text-foreground">
                    {interviews?.filter(i => {
                      const interviewDate = new Date(i.dateTime);
                      const now = new Date();
                      const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
                      return interviewDate >= now && interviewDate <= weekFromNow;
                    })?.length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            {/* Calendar Section - 8 columns */}
            <div className="xl:col-span-8">
              <CalendarView
                selectedDate={selectedDate}
                onDateSelect={handleDateSelect}
                interviews={interviews}
                onInterviewClick={handleInterviewClick}
                onTimeSlotSelect={handleTimeSlotSelect}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
              />
            </div>

            {/* Right Panel - 4 columns */}
            <div className="xl:col-span-4 space-y-6">
              {/* Scheduling Form */}
              <SchedulingForm
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                onScheduleInterview={handleScheduleInterview}
                onCancel={handleCancelEdit}
                editingInterview={editingInterview}
              />

              {/* Upcoming Interviews */}
              <UpcomingInterviews
                interviews={interviews}
                onReschedule={handleRescheduleInterview}
                onCancel={handleCancelInterview}
                onSendReminder={handleSendReminder}
                onEdit={handleEditInterview}
              />
            </div>
          </div>
        </div>
      </main>
      {/* Bulk Scheduling Modal */}
      {showBulkScheduling && (
        <BulkScheduling
          onBulkSchedule={handleBulkSchedule}
          onClose={() => setShowBulkScheduling(false)}
        />
      )}
    </div>
  );
};

export default InterviewScheduling;